import { AlertDialog, AlertDialogAction, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { CreditCard, Crown, Ban, AlertTriangle, Mail } from "lucide-react"

interface AccessDeniedModalProps {
  isOpen: boolean
  onClose: () => void
  errorCode: string
  errorMessage: string
  subscriptionStatus?: string
  subscriptionTier?: string
  expiresAt?: string
  upgradeRequired?: boolean
  premiumRequired?: boolean
  supportContact?: boolean
}

export function AccessDeniedModal({
  isOpen,
  onClose,
  errorCode,
  errorMessage,
  subscriptionStatus,
  subscriptionTier,
  expiresAt,
  upgradeRequired,
  premiumRequired,
  supportContact
}: AccessDeniedModalProps) {

  const getIcon = () => {
    switch (errorCode) {
      case 'SUBSCRIPTION_REQUIRED':
        return <CreditCard className="h-12 w-12 text-orange-500" />
      case 'PREMIUM_REQUIRED':
        return <Crown className="h-12 w-12 text-yellow-500" />
      case 'ACCOUNT_DISABLED':
        return <Ban className="h-12 w-12 text-red-500" />
      default:
        return <AlertTriangle className="h-12 w-12 text-red-500" />
    }
  }

  const getTitle = () => {
    switch (errorCode) {
      case 'SUBSCRIPTION_REQUIRED':
        return 'Aktives Abonnement erforderlich'
      case 'PREMIUM_REQUIRED':
        return 'Premium-Abonnement erforderlich'
      case 'ACCOUNT_DISABLED':
        return 'Zugang gesperrt'
      default:
        return 'Zugang verweigert'
    }
  }

  const getActionText = () => {
    if (supportContact) return 'Support kontaktieren'
    if (premiumRequired) return 'Auf Premium upgraden'
    if (upgradeRequired) return 'Abonnement aktivieren'
    return 'Verstanden'
  }

  const handleAction = () => {
    if (supportContact) {
      window.open('mailto:support@linkedinlina.de?subject=Account%20Access%20Issue', '_blank')
    } else if (upgradeRequired || premiumRequired) {
      // In a real app, redirect to billing/upgrade page
      console.log('Redirect to billing page')
    }
    onClose()
  }

  return (
    <AlertDialog open={isOpen} onOpenChange={onClose}>
      <AlertDialogContent className="sm:max-w-md">
        <AlertDialogHeader className="text-center">
          <div className="mx-auto mb-4">
            {getIcon()}
          </div>
          <AlertDialogTitle className="text-lg font-semibold">
            {getTitle()}
          </AlertDialogTitle>
          <AlertDialogDescription className="text-center space-y-3">
            <p>{errorMessage}</p>
            
            {subscriptionStatus && (
              <div className="space-y-2">
                <div className="flex justify-center gap-2">
                  <Badge variant="outline" className="text-xs">
                    Status: {subscriptionStatus}
                  </Badge>
                  <Badge variant="outline" className="text-xs">
                    Tarif: {subscriptionTier}
                  </Badge>
                </div>
                
                {expiresAt && (
                  <p className="text-xs text-muted-foreground">
                    Läuft ab: {new Date(expiresAt).toLocaleDateString('de-DE')}
                  </p>
                )}
              </div>
            )}

            {supportContact && (
              <div className="bg-muted/50 p-3 rounded-md">
                <div className="flex items-center justify-center gap-2 text-sm">
                  <Mail className="h-4 w-4" />
                  support@linkedinlina.de
                </div>
              </div>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        
        <AlertDialogFooter className="sm:justify-center">
          <AlertDialogAction asChild>
            <Button 
              onClick={handleAction}
              variant={upgradeRequired || premiumRequired ? "default" : "outline"}
              data-testid="button-access-denied-action"
            >
              {getActionText()}
            </Button>
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}